import React from 'react';
import { Link } from 'react-router';
import { ArrowRight } from 'lucide-react';

/**
 * SEO Internal Links Component
 * Provides structured internal links to related content and geo pages
 * Used for Topic Clustering strategy per Google 2026 guidelines
 */
export const InternalLinksSection = ({ currentPage = '', variant = 'full' }) => {
  const geoPages = [
    { path: '/unfallgutachten-rellingen', name: 'Rellingen (Hauptsitz)' },
    { path: '/unfallgutachten-hamburg', name: 'Hamburg' },
    { path: '/unfallgutachten-norderstedt', name: 'Norderstedt' },
    { path: '/unfallgutachten-pinneberg', name: 'Pinneberg' },
    { path: '/unfallgutachten-ahrensburg', name: 'Ahrensburg' },
    { path: '/unfallgutachten-mainz', name: 'Mainz' },
    { path: '/unfallgutachten-frankfurt', name: 'Frankfurt' },
    { path: '/unfallgutachten-wiesbaden', name: 'Wiesbaden' },
  ];

  const ratgeberPages = [
    { 
      path: '/ratgeber/nach-unfall-checkliste', 
      title: 'Nach dem Unfall: Checkliste', 
      desc: 'Schritt-für-Schritt was zu tun ist' 
    },
    { 
      path: '/ratgeber/wertminderung', 
      title: 'Wertminderung nach Unfall', 
      desc: 'Anspruch & Berechnung erklärt' 
    },
    { 
      path: '/ratgeber/gutachten-oder-kostenvoranschlag', 
      title: 'Gutachten vs. Kostenvoranschlag', 
      desc: 'Was ist der Unterschied?' 
    },
  ];

  const filteredGeoPages = geoPages.filter(page => page.path !== currentPage);
  const filteredRatgeberPages = ratgeberPages.filter(page => page.path !== currentPage);

  if (variant === 'compact') {
    return (
      <div className="bg-gray-50 rounded-2xl p-6 mt-8">
        <h4 className="font-bold text-gray-900 mb-4">Gutachten in Ihrer Region</h4>
        <div className="flex flex-wrap gap-2">
          {filteredGeoPages.map(page => (
            <Link 
              key={page.path}
              to={page.path} 
              className="text-sm px-3 py-1.5 bg-white rounded-lg border border-gray-200 hover:border-brand-green hover:text-brand-green transition-all"
            >
              {page.name}
            </Link>
          ))}
          <Link 
            to="/bundesweit" 
            className="text-sm px-3 py-1.5 bg-brand-green/10 rounded-lg border border-brand-green/30 text-brand-green hover:bg-brand-green hover:text-white transition-all"
          >
            Bundesweit
          </Link>
        </div>
      </div>
    );
  }

  return (
    <section className="py-16 bg-white border-t border-gray-200">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Related Ratgeber */}
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-6">Hilfreiche Ratgeber</h3>
            <div className="space-y-4">
              {filteredRatgeberPages.map(page => (
                <Link 
                  key={page.path}
                  to={page.path} 
                  className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-brand-green hover:shadow-md transition-all group"
                >
                  <ArrowRight className="w-5 h-5 text-brand-green group-hover:translate-x-1 transition-transform flex-shrink-0" />
                  <div>
                    <span className="font-semibold text-gray-900 group-hover:text-brand-green transition-colors">{page.title}</span>
                    <p className="text-sm text-gray-600">{page.desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
          
          {/* Other Locations */}
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-6">Weitere Standorte</h3>
            <div className="flex flex-wrap gap-3">
              {filteredGeoPages.map(page => (
                <Link 
                  key={page.path}
                  to={page.path} 
                  className="px-4 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:border-brand-green hover:text-brand-green transition-all text-sm font-medium"
                >
                  {page.name}
                </Link>
              ))}
              <Link 
                to="/bundesweit" 
                className="px-4 py-2 bg-brand-green/10 rounded-lg border border-brand-green/30 text-brand-green transition-all text-sm font-medium hover:bg-brand-green hover:text-white"
              >
                Bundesweit
              </Link>
            </div>
            
            <p className="text-sm text-gray-500 mt-6">
              Zuletzt aktualisiert: 5. Februar 2026
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InternalLinksSection;
